import { gql } from 'graphql-request';
import { Customer, SamplingCadence, Site } from 'src/shared/types';
import { Project, ProjectFilters, ProjectDefinition } from 'src/shared/types';

export const projectsQuery = gql`
    query getProjectList($customerId: String!) {
        customerWithProjects: customer(customerId: $customerId) {
            projects {
                projectId
                projectName
                projectType
                samplesCount
                subscription {
                    name
                    label
                    allowedResources
                }
                features
                samplingCadence {
                    type
                }
                status
            }
        }
    }
`;
export type CustomerProject = Pick<
    ProjectDefinition,
    'projectId' | 'projectName' | 'projectCode' | 'samplesCount' | 'subscription' | 'features' | 'projectType' | 'status'
> & {
    samplingCadence: Pick<SamplingCadence, 'type'>;
};

export type CustomerProjects = CustomerProject[];

export type ProjectsQueryResult = {
    customerWithProjects: {
        projects: CustomerProjects;
    };
};

export const projectForAdminQuery = gql`
    query getProjectList($customerId: String!) {
        customer(customerId: $customerId) {
            projects {
                projectId
                projectName
                projectCode
                country
                createdAt
                creatorName
                status
                samplesCount
                habitatAssay {
                    habitatName
                    assayName
                    habitatAssayKey
                }
                features
                updatedAt
                updaterName
            }
        }
    }
`;

export type ProjectForAdmin = Pick<
    ProjectDefinition,
    | 'projectCode'
    | 'projectName'
    | 'habitatAssay'
    | 'country'
    | 'createdAt'
    | 'creatorName'
    | 'projectId'
    | 'status'
    | 'samplesCount'
    | 'features'
    | 'updatedAt'
    | 'updaterName'
>;

export type ProjectsForAdminQueryResult = {
    customer: {
        projects: ProjectForAdmin[];
    };
};

export const projectIdsQuery = gql`
    query getProjectIds($customerId: String!) {
        customer(customerId: $customerId) {
            projects {
                projectId
            }
        }
    }
`;

export const projectQuery = gql`
    query projects($projectId: String!) {
        project(projectId: $projectId) {
            projectId
            projectName
            projectStartDate
            latestReportingDate
            griisLookupCountries
            samples {
                location {
                    coordinates
                }
                sampleId
                sampleCollectedDatetime
                sampleDeployDatetime
            }
        }
    }
`;

export type ProjectQueryResult = {
    project: Project;
};

export const projectFiltersQuery = gql`
    query getFilters($projectId: String!) {
        filters(projectId: $projectId) {
            map {
                times
                areas
                habitatAssays
                hlcaSites
                hlcaChartTypes
            }
        }
    }
`;

export type ProjectFiltersQueryResult = {
    filters: ProjectFilters;
};

export const customerProjectsInBCQuery = gql`
    query getCustomertListWithProjects($customerId: String!) {
        customerProjectsInBC(customerId: $customerId) {
            customerId
            customerName
            projects {
                projectId
                projectName
                projectCode
                leadName
                leadEmail
            }
        }
    }
`;

export const enablePdfDownloadQuery = gql`
    mutation ($projectId: String!, $eventName: String!) {
        admin {
            enablePdfDownload(projectId: $projectId, eventName: $eventName)
        }
    }
`;

export type CustomerProjectsInBCQueryResult = {
    customerProjectsInBC: Pick<Customer, 'customerId' | 'customerName' | 'projects'>;
};

export type GetProjectSamplingCadenceQueryResult = {
    getProjectSamplingCadence: {
        samplingCadence: SamplingCadence;
    };
};

export const getProjectSamplingCadenceQuery = gql`
    query getProjectSamplingCadence($projectId: String!) {
        getProjectSamplingCadence: getProjectSummary(projectId: $projectId) {
            samplingCadence {
                type
                samplingEventsPerYear
                events {
                    identifier
                    name
                    fromDate
                    toDate
                    year
                    processed
                    processedDate
                    status
                }
            }
        }
    }
`;

export const projectSitesQuery = gql`
    query getProjectSites($projectId: String!) {
        getProjectSites: getProjectSummary(projectId: $projectId) {
            sites {
                type
                uploadedAt
                uploadedBy
                geometry {
                    coordinates
                }
                properties {
                    name
                    area
                    status
                    statusMessage
                    metrics {
                        metric
                        layers {
                            year
                            status
                            url
                            boundaryUrl
                        }
                    }
                }
            }
        }
    }
`;

export type ProjectSitesQueryResult = {
    project: {
        sites: Site[];
    };
};

export const projectDefinitionQuery = gql`
    query getProjectDefinition($projectId: String!) {
        projectDefinition: getProjectSummary(projectId: $projectId) {
            projectId
            customerName
            projectName
            projectCode
            files(fileType: "reports") {
                name
                awsFileKey
                fileCreationDate
            }
            subscription {
                label
                name
                allowedResources
            }
            country
            leadEmail
            leadName
            area
            leadRole
            projectType
            targetOutcome
            samplingCadence {
                type
                samplingEventsPerYear
                events {
                    identifier
                    name
                    fromDate
                    toDate
                    year
                    processed
                    processedDate
                    status
                }
            }
            projectLifespanYears
            sampleGroups
            speciesOfInterest
            habitatAssay {
                habitatName
                assayName
                habitatAssayKey
            }
            projectEndDate
            projectStartDate
            estimatedSampleCounts
            estimatedSampleCountForTestTypes {
                type {
                    assayName
                    habitatAssayKey
                    habitatName
                }
                count
            }
            createdAt
            updatedAt
            subscriptionType
            samplesCount
            features
            griisLookupCountries
            isChegdProject
            sampleManifestStatus
            notifyUsersOnChange
            hasDefinedSampleEvents
            status
        }
    }
`;

export type ProjectDefinitionQueryResult = Pick<
    ProjectDefinition,
    | 'projectId'
    | 'customerName'
    | 'projectName'
    | 'projectCode'
    | 'subscription'
    | 'country'
    | 'leadEmail'
    | 'leadName'
    | 'area'
    | 'leadRole'
    | 'projectType'
    | 'targetOutcome'
    | 'samplingCadence'
    | 'projectLifespanYears'
    | 'sampleGroups'
    | 'speciesOfInterest'
    | 'habitatAssay'
    | 'projectStartDate'
    | 'projectEndDate'
    | 'estimatedSampleCounts'
    | 'estimatedSampleCountForTestTypes'
    | 'createdAt'
    | 'updatedAt'
    | 'files'
    | 'subscriptionType'
    | 'samplesCount'
    | 'features'
    | 'griisLookupCountries'
    | 'isChegdProject'
    | 'sampleManifestStatus'
    | 'notifyUsersOnChange'
    | 'hasDefinedSampleEvents'
    | 'status'
>;

export const deleteProjectDefinitionQuery = gql`
    mutation deleteProjectDefinition($projectId: String!) {
        deleteProjectDefinition(projectId: $projectId)
    }
`;
